'use client';

import React, { type HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import customCss from '@/app/components/hotel-detail/css/customCss';
import {
  type ExtendedSortedService,
  getServicePreviewLabel,
  useExtendedSortedHotelServices,
} from '@/app/components/hotel-detail/hooks/useExtendedSortedHotelServices';
import { HotelPageContentSection } from '@/app/components/hotel-detail/HotelPageContentSection';
import { useTranslation } from '@/app/components/translations/Translation';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import { Icon } from '@/app/common/icons/Icon';
import { FACILITY_ICONS } from '@/app/components/hotel-detail/hooks/useExtendedSortedHotelServices';

type EvoHotelFacilitiesProps = HTMLAttributes<HTMLElement> & {
  // Force a single column at every width. Defaults to false, i.e. two columns from `sm` up.
  singleColumn?: boolean;
};

const MostPopularTitle = styled(Text).attrs({ variant: 'Title5' })<{ $singleColumn?: boolean }>`
  /* space150 (12px) below the heading on mobile/single-column, space100 (8px) otherwise */
  margin-bottom: ${({ theme, $singleColumn }) =>
    $singleColumn ? theme.kallisto.space.space150 : theme.kallisto.space.space100};
`;

const Separator = styled.hr`
  margin: 24px 0;
  border: 0;
  border-top: 1px solid ${(p) => p.theme.commons.colors.gray100};
`;

// Full list in two columns from `sm` up (single column below, or always when singleColumn is set).
const CategoriesGrid = styled.div<{ $singleColumn?: boolean }>`
  /* space500 between the two columns */
  column-gap: ${({ theme }) => theme.kallisto.space.space500};

  ${({ $singleColumn }) =>
    !$singleColumn &&
    css`
      @media ${customCss.breakpoints.sm} {
        column-count: 2;
      }
    `}
`;

// GENERAL is the long catch-all category, so let it flow across columns; every other
// category stays intact within a single column.
const CategoryContainer = styled.div<{ $breakColumn: boolean; $singleColumn?: boolean }>`
  display: flex;
  flex-direction: column;
  /* space150 (12px) on mobile/single-column, space200 (16px) otherwise, between the title and its list */
  gap: ${({ theme, $singleColumn }) => ($singleColumn ? theme.kallisto.space.space150 : theme.kallisto.space.space200)};
  /* space300 between stacked categories within a column */
  padding-bottom: ${({ theme }) => theme.kallisto.space.space300};

  ${({ $breakColumn }) => ($breakColumn ? 'break-inside: auto' : 'break-inside: avoid')};
`;

const CategoryTitle = styled(Text).attrs({ variant: 'Title5' })`
  display: flex;
  align-items: center;
  /* space100 between the icon and the label */
  gap: ${({ theme }) => theme.kallisto.space.space100};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ServiceList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  /* space50 between services */
  li:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.kallisto.space.space50};
  }
`;

// The compact preview row: one entry per category (its first facility, or the category label
// when it has none), each with its icon.
export function MostPopularRow({ services }: { services: ExtendedSortedService[] }) {
  const { t } = useTranslation();

  return (
    <ul
      css={css`
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        /* space100 (8px) row gap, space250 (20px) column gap */
        gap: 8px 20px;
        margin: 0;
        padding: 0;
        list-style: none;
      `}
    >
      {services.map((service) => (
        <li
          key={service.category}
          css={`
            display: flex;
            gap: 8px;
            align-items: center;
          `}
        >
          <Icon {...FACILITY_ICONS[service.icon]} size={20} />
          <Text variant="Body">{getServicePreviewLabel(service, t)}</Text>
        </li>
      ))}
    </ul>
  );
}

// A single category: its heading (icon + label) and its list of services in Body-regular text,
// with no per-service check icon.
function FacilitiesCategory({ category, singleColumn }: { category: ExtendedSortedService; singleColumn?: boolean }) {
  const { t } = useTranslation();

  return (
    <CategoryContainer $breakColumn={category.category === 'GENERAL'} $singleColumn={singleColumn}>
      <CategoryTitle>
        <Icon {...FACILITY_ICONS[category.icon]} size={20} />
        {t(category.translationKey)}
      </CategoryTitle>
      <ServiceList>
        {category.services.map((service) => (
          <li key={service}>
            <Text variant="Body">{service}</Text>
          </li>
        ))}
      </ServiceList>
    </CategoryContainer>
  );
}

/**
 * A simplified facilities section: the "Most popular" preview row plus the full list of
 * facilities grouped by category, shown inline. Compared with {@link HotelPageFacilitiesSection}
 * it drops the "see more" modal / show-more button, removes the per-service check icon, and
 * renders services in Body-regular text.
 */
export default function EvoHotelFacilities({ singleColumn, ...props }: EvoHotelFacilitiesProps) {
  const { t } = useTranslation();
  const { data: staticData } = useFetchHotelStatic();
  const services = useExtendedSortedHotelServices(staticData?.services);

  if (!services || services.length === 0) return null;

  return (
    <HotelPageContentSection {...props} title={t('vacation.select.facilities.title')}>
      <MostPopularTitle $singleColumn={singleColumn}>{t('vacation.select.facilities.popular')}</MostPopularTitle>
      <MostPopularRow services={services} />
      <Separator />
      <CategoriesGrid $singleColumn={singleColumn}>
        {services.map((category) => (
          <FacilitiesCategory key={category.category} category={category} singleColumn={singleColumn} />
        ))}
      </CategoriesGrid>
    </HotelPageContentSection>
  );
}
